import { useEffect, useState } from "react";
import { Check, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useLoad, useSubmit } from "@/lib/api";
import { ErrorBox, Field, PageHeading } from "@/components/common";
import { notifyClinicUpdated } from "@/lib/exportData";
import WeeklyBackup from "@/components/WeeklyBackup";

const FIELDS = [["name", "Clinic name"], ["phone", "Phone"], ["email", "Email"], ["registration_no", "Registration / GST no."]];

function LogoPicker({ logo, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/settings/logo", fd); onChange(r.data.logo_data_url); notifyClinicUpdated(); toast.success("Logo updated"); }
    catch (e) { toast.error(errMsg(e, "Could not upload the logo")); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete("/settings/logo"); onChange(""); notifyClinicUpdated(); toast.success("Logo removed"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="logo-row" data-testid="clinic-logo-section">
      <div className="logo-preview">{logo ? <img src={logo} alt="Clinic logo" data-testid="clinic-logo-preview" /> : <ImagePlus size={22} />}</div>
      <div className="row-actions">
        <label className="secondary file-btn" data-testid="clinic-logo-upload-button"><ImagePlus size={15} /> {busy ? "Uploading…" : logo ? "Change logo" : "Upload logo"}<input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => upload(e.target.files?.[0])} data-testid="clinic-logo-input" /></label>
        {logo && <button type="button" className="icon-btn" onClick={remove} title="Remove logo" data-testid="clinic-logo-remove-button"><Trash2 size={15} /></button>}
      </div>
      <small className="muted small-note">PNG, JPG or WEBP under 500 KB. Shows on receipts, the day-end report and the sidebar.</small>
    </div>
  );
}

export default function Settings() {
  const { data, loading } = useLoad("/settings/clinic", null);
  const [form, setForm] = useState(null);
  const { busy, error, run } = useSubmit();
  useEffect(() => { if (data) setForm(data); }, [data]);
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const save = (e) => { e.preventDefault(); run(async () => { const r = await api.put("/settings/clinic", form); setForm(r.data); notifyClinicUpdated(); toast.success("Clinic details saved"); }); };
  return (
    <div className="content">
      <PageHeading eyebrow="SETTINGS / CLINIC DETAILS" title="Your clinic," accent="on every receipt." sub="These details print at the top of payment receipts, refund vouchers and reports." />
      {loading || !form ? <p className="muted">Loading…</p> : <>
        <form className="settings-card" onSubmit={save} data-testid="clinic-settings-form">
          <LogoPicker logo={form.logo_data_url} onChange={set("logo_data_url")} />
          <div className="form-two">{FIELDS.map(([k, l]) => <Field key={k} label={l} testid={`clinic-${k}-input`} required={k === "name"} value={form[k]} onChange={set(k)} />)}</div>
          <Field label="Address" textarea rows={2} testid="clinic-address-input" value={form.address} onChange={set("address")} />
          <Field label="Receipt footer message" testid="clinic-footer-input" value={form.receipt_footer} onChange={set("receipt_footer")} />
          <div className="form-two"><Field label="WhatsApp country code" testid="clinic-country-code-input" value={form.country_code} onChange={set("country_code")} placeholder="91" /></div>
          <Field label="Appointment reminder message" textarea rows={3} testid="clinic-reminder-template-input" value={form.reminder_template} onChange={set("reminder_template")} />
          <p className="muted small-note">Placeholders: {"{name} {clinic} {date} {time} {purpose} {phone}"}</p>
          <ErrorBox error={error} testid="clinic-settings-error" />
          <button className="primary" disabled={busy} data-testid="save-clinic-settings-button"><Check size={17} /> {busy ? "Saving…" : "Save clinic details"}</button>
        </form>
        <WeeklyBackup />
      </>}
    </div>
  );
}
